import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { CreateProductDto, Product } from '../../services/inventory.service';
import {
  Field,
  FormActions,
  FormSection,
  formatMoney,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';

interface ProductFormProps {
  product?: Product;
  onSubmit: (data: CreateProductDto) => void;
  isLoading: boolean;
  onCancel: () => void;
  // Existing categories, offered as suggestions
  categories?: string[];
}

const UNIT_OPTIONS = [
  { value: 'pcs', label: 'Pieces (pcs)' },
  { value: 'pieces', label: 'Pieces' },
  { value: 'kg', label: 'Kilograms' },
  { value: 'ltr', label: 'Liters' },
  { value: 'mtr', label: 'Meters' },
  { value: 'box', label: 'Box' },
  { value: 'set', label: 'Set' },
];

const ProductForm: React.FC<ProductFormProps> = ({
  product,
  onSubmit,
  isLoading,
  onCancel,
  categories = [],
}) => {
  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { errors },
  } = useForm<CreateProductDto>({
    defaultValues: product
      ? {
          name: product.name,
          sku: product.sku,
          barcode: product.barcode ?? '',
          description: product.description,
          category: product.category,
          unit: product.unit,
          price: Number(product.price),
          costPrice: Number(product.costPrice),
          unitPrice: Number(product.unitPrice),
          currentStock: product.currentStock,
          minimumStock: product.minimumStock,
          maximumStock: product.maximumStock,
          location: product.location,
          hsnCode: product.hsnCode ?? '',
        }
      : {},
  });

  const [price, costPrice] = useWatch({ control, name: ['price', 'costPrice'] });
  const margin = Number(price) - Number(costPrice);
  const hasMargin = Number(price) > 0 && Number.isFinite(margin) && Number(costPrice) >= 0;

  // Keep a unit that is not in the standard list selectable when editing
  const unitOptions =
    product?.unit && !UNIT_OPTIONS.some((option) => option.value === product.unit)
      ? [...UNIT_OPTIONS, { value: product.unit, label: product.unit }]
      : UNIT_OPTIONS;

  const fieldClass = (hasError: boolean) => cn(inputClass, hasError && inputErrorClass);
  // Stock is kept to four decimals, so kilograms, metres and litres can be entered exactly
  const wholeNumber = (value: number) => Math.abs(Math.round(value * 10000) / 10000 - value) < 1e-9 || 'Use at most 4 decimals';

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
      <FormSection title="Product details">
        <Field label="Product Name" htmlFor="name" required error={errors.name?.message}>
          <input
            {...register('name', { required: 'Product name is required' })}
            type="text"
            id="name"
            className={fieldClass(!!errors.name)}
            placeholder="e.g. Steel Rod 10mm"
          />
        </Field>

        <Field label="SKU" htmlFor="sku" required error={errors.sku?.message}>
          <input
            {...register('sku', { required: 'SKU is required' })}
            type="text"
            id="sku"
            className={cn(fieldClass(!!errors.sku), 'font-mono')}
            placeholder="e.g. STL-ROD-10"
          />
        </Field>

        <Field label="Maker's barcode" htmlFor="barcode" error={errors.barcode?.message} hint="EAN or UPC printed on the item, if it has one. Scanning it finds this item.">
          <input
            {...register('barcode', { pattern: { value: /^[\x21-\x7E]{0,50}$/, message: 'Up to 50 letters, digits or symbols, without spaces' } })}
            type="text"
            id="barcode"
            className={cn(fieldClass(!!errors.barcode), 'font-mono')}
            placeholder="e.g. 8901234567893"
          />
        </Field>

        <Field label="Category" htmlFor="category" required error={errors.category?.message}>
          <input
            {...register('category', { required: 'Category is required' })}
            type="text"
            id="category"
            list="product-category-options"
            className={fieldClass(!!errors.category)}
            placeholder="e.g. Raw Materials"
          />
          <datalist id="product-category-options">
            {categories.map((category) => (
              <option key={category} value={category} />
            ))}
          </datalist>
        </Field>

        <Field label="Unit" htmlFor="unit" required error={errors.unit?.message}>
          <select
            {...register('unit', { required: 'Unit is required' })}
            id="unit"
            className={fieldClass(!!errors.unit)}
          >
            <option value="">Select unit</option>
            {unitOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Description" htmlFor="description" className="sm:col-span-2">
          <textarea
            {...register('description')}
            id="description"
            rows={2}
            className={inputClass}
            placeholder="Short description of the product"
          />
        </Field>
      </FormSection>

      <FormSection title="Pricing" columns={3}>
        <Field label="Selling Price (₹)" htmlFor="price" required error={errors.price?.message}>
          <input
            {...register('price', {
              required: 'Price is required',
              min: { value: 0, message: 'Price cannot be negative' },
              valueAsNumber: true,
            })}
            type="number"
            step="0.01"
            min="0"
            id="price"
            className={fieldClass(!!errors.price)}
            placeholder="0.00"
          />
        </Field>

        <Field label="Cost Price (₹)" htmlFor="costPrice" required error={errors.costPrice?.message}>
          <input
            {...register('costPrice', {
              required: 'Cost price is required',
              min: { value: 0, message: 'Cost price cannot be negative' },
              valueAsNumber: true,
            })}
            type="number"
            step="0.01"
            min="0"
            id="costPrice"
            className={fieldClass(!!errors.costPrice)}
            placeholder="0.00"
          />
        </Field>

        <Field label="Unit Price (₹)" htmlFor="unitPrice" required error={errors.unitPrice?.message}>
          <input
            {...register('unitPrice', {
              required: 'Unit price is required',
              min: { value: 0, message: 'Unit price cannot be negative' },
              valueAsNumber: true,
            })}
            type="number"
            step="0.01"
            min="0"
            id="unitPrice"
            className={fieldClass(!!errors.unitPrice)}
            placeholder="0.00"
          />
        </Field>

        {hasMargin && (
          <p className="text-sm text-gray-600 sm:col-span-2 lg:col-span-3">
            Margin per unit:{' '}
            <span className={cn('font-semibold', margin < 0 ? 'text-red-600' : 'text-gray-900')}>
              {formatMoney(margin)} ({((margin / Number(price)) * 100).toFixed(1)}%)
            </span>
            {margin < 0 && ' — the selling price is below cost'}
          </p>
        )}
      </FormSection>

      <FormSection title="Stock levels" columns={4}>
        <Field
          label="Current Stock"
          htmlFor="currentStock"
          required
          error={errors.currentStock?.message}
          hint={product ? 'Use Add or Adjust Stock to keep a history.' : undefined}
        >
          <input
            {...register('currentStock', {
              required: 'Current stock is required',
              min: { value: 0, message: 'Stock cannot be negative' },
              validate: wholeNumber,
              valueAsNumber: true,
            })}
            type="number"
            min="0"
            step="0.0001"
            id="currentStock"
            className={fieldClass(!!errors.currentStock)}
            placeholder="0"
          />
        </Field>

        <Field label="Minimum Stock" htmlFor="minimumStock" required error={errors.minimumStock?.message}>
          <input
            {...register('minimumStock', {
              required: 'Minimum stock is required',
              min: { value: 0, message: 'Minimum stock cannot be negative' },
              validate: wholeNumber,
              valueAsNumber: true,
            })}
            type="number"
            min="0"
            step="0.0001"
            id="minimumStock"
            className={fieldClass(!!errors.minimumStock)}
            placeholder="0"
          />
        </Field>

        <Field label="Maximum Stock" htmlFor="maximumStock" required error={errors.maximumStock?.message}>
          <input
            {...register('maximumStock', {
              required: 'Maximum stock is required',
              min: { value: 0, message: 'Maximum stock cannot be negative' },
              validate: {
                wholeNumber,
                aboveMinimum: (value) =>
                  value >= Number(getValues('minimumStock') || 0) ||
                  'Maximum must not be below the minimum',
              },
              valueAsNumber: true,
            })}
            type="number"
            min="0"
            step="0.0001"
            id="maximumStock"
            className={fieldClass(!!errors.maximumStock)}
            placeholder="0"
          />
        </Field>

        <Field label="Bin / rack" htmlFor="location" hint="Where it sits inside the store">
          <input
            {...register('location')}
            type="text"
            id="location"
            className={inputClass}
            placeholder="e.g. A-01-01"
          />
        </Field>

        <Field label="HSN / SAC code" htmlFor="hsnCode" error={errors.hsnCode?.message} hint="4, 6 or 8 digits. Used on GST invoices">
          <input
            {...register('hsnCode', {
              setValueAs: (value) => (typeof value === 'string' ? value.trim() : value),
              validate: (value) => !value || /^(\d{4}|\d{6}|\d{8})$/.test(value) || 'The HSN code must be 4, 6 or 8 digits',
            })}
            type="text"
            inputMode="numeric"
            maxLength={8}
            id="hsnCode"
            className={fieldClass(!!errors.hsnCode)}
            placeholder="e.g. 73181500"
          />
        </Field>
      </FormSection>

      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isLoading} className={primaryButtonClass}>
          {isLoading ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
        </button>
      </FormActions>
    </form>
  );
};

export default ProductForm;
